import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { getProcedureRevisionsForPart, getRevForPart } from '../../../redux/procedureRevisionSlice';
import RevisionTable from '../components/RevisionTable';
import DocumentAppBar from '../components/DocumentAppBar';
import DocumentHeader from '../components/DocumentHeader';
import useCurrentRevisionData from '../hooks/useCurrentRevisionData';
import TrainingLogTable from '../components/TrainingLogTable';
import { isLocked,revisionSort } from '../Utils/utils';
import useDocumentData from '../hooks/useDocumentData';
import BOMTable from '../components/BOMTable';
import RenderRevisionNodes from '../components/RenderRevisionNodes';
import InlineLoader from '../../../common/InlineLoader';
import { useNavigate } from 'react-router-dom';

const MagicView = () => {
  const { item_code } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  
  const { revisionData, error, noRevisions } = useSelector(
    (state) => state.procedureRevision
  );


  const [revisionToShow, setRevisionToShow] = useState(null);
  
  // Get current revision and status using custom hook
  const { currentRevision, currentStatus } = useCurrentRevisionData(
    revisionData,
    revisionToShow?.procedure_revision_id
  );


  
  /**Old Validation part */
  // if(noRevisions){
  //   navigate("/not-found")
  // }else if(location.pathname.includes("/live/")){
  //   if(currentStatus !== "Released" && currentStatus !== null){
  //     navigate("/not-found")
  //   }
  // }
  // else if(location.pathname.includes("/edit/")){
  //   if(currentStatus === "Released"){
  //     navigate("/not-found")
  //   }
  // }
    
  // useEffect(() => {
  //   if (item_code) {
  //     dispatch(getProcedureRevisionsForPart({ part_number: item_code }));
  //   }
  // }, [dispatch, item_code]);

  useEffect(() => {

    (async()=>{
      if (item_code) {

        const resp = await dispatch(getProcedureRevisionsForPart({ part_number: item_code }));
        const revData = resp.payload.results

        if(revData.length === 0){
          navigate("/not-found?fromMagicLink=true")
          return
        }
        
        let revStatusArray = revData.map(r=>{
          return {
            procedure_revision_id:r.procedure_revision_id,
            status:r.status_changes?.find(s=>s.is_latest)?.status_changed_to,
            rev:r.rev,
          }
        })

        revStatusArray = revStatusArray.sort(revisionSort);

        if(location.pathname.includes("/edit/")){

          let redirectableRevs = revStatusArray.filter(lr=>lr.status !== "Rejected" && lr.status !== "MAINTAINED OUTSIDE OF SYSTEM")

          if(redirectableRevs.length === 0){
            navigate("/not-found?fromMagicLink=true")
            return
          }
          
          let latestRev  = redirectableRevs[redirectableRevs.length-1]

          setRevisionToShow(revData.find(r=>r.procedure_revision_id === latestRev.procedure_revision_id))

        }
        else if(location.pathname.includes("/live/")){
          let redirectableRevs = revStatusArray.filter(lr=>lr.status === "Released")

          if(redirectableRevs.length === 0){
            navigate("/not-found?fromMagicLink=true")
            return
          }
          
          let latestRev  = redirectableRevs[redirectableRevs.length-1]

          setRevisionToShow(revData.find(r=>r.procedure_revision_id === latestRev.procedure_revision_id))
        }
      }
    })()


  }, [item_code]);

  const isLock = isLocked(currentStatus);

  // Store all nodes data for caching in preview
  const [allNodesData, setAllNodesData] = useState([]);

  // Fetch all document data using custom hook
  const {
    // revisionData,
    trainingLogs,
    users,
    approvedUsers,
    releasedUsers,
    rule,
    bomTable,
    loading,
    refreshRevisionData,
    refreshBomTable,
  }  = useDocumentData(revisionToShow?.part_ref, revisionToShow?.procedure_revision_id, true);
  
  // Compute BOM table data locally if locked
  const bomTableData = useMemo(() => {
      if (!isLock) return bomTable;
  
      // Return empty if no nodes data is available yet
      if (!allNodesData) return [];
  
      const bomAggregator = {};
  
      const traverse = (nodes) => {
        if (!Array.isArray(nodes)) return;
  
        nodes.forEach((node) => {
          // Check for BOM rows in the current node (step)
          // Similar logic to StepNode.js handling past_step_data_json
          const pastData = node.detail?.past_step_data_json;
  
          if (pastData?.bom_rows && Array.isArray(pastData.bom_rows)) {
            pastData.bom_rows.forEach((row) => {
              const partNumber = row.partNumber;
              // Skip invalid rows
              if (!partNumber) return;
  
              const qty = parseFloat(row.qty || 0);
  
              if (!bomAggregator[partNumber]) {
                // Initialize entry mapped to BOM flat format
                bomAggregator[partNumber] = {
                  type_name: row.type,
                  part_number: row.partNumber,
                  part_description: row.description,
                  qty: qty,
                  unit_name: row.unit_name,
                  // Preserve additional fields for sorting
                  orders: row.type_orders ?? row.part_info?.type_info?.orders ?? row.part_info?.part_info?.type_info?.orders
                };
              } else {
                // Aggregate Quantity
                bomAggregator[partNumber].qty += qty;
              }
            });
          }
  
          // Recursively traverse children
          if (node.steps) traverse(node.steps);
          if (node.substeps) traverse(node.substeps);
        });
      };
  
      traverse(allNodesData);
  
      return Object.values(bomAggregator);
  }, [isLock, bomTable, allNodesData]);

  return (<>
    <div style={{ paddingBottom: "20px" }}>  
      <DocumentAppBar
        partNumber={currentRevision?.part_number}
        partId={currentRevision?.procedure_id}
        type={currentRevision?.type_name}
        description={currentRevision?.description}
        revision={currentRevision?.rev}
        users={users}
        approvedUsers={approvedUsers}
        releasedUsers={releasedUsers}
        rule={rule}
        onRevisionCreated={null}
        onCloseAdditionalInfo={null}
        showNavLink={false}
        showTablesDropdown={false}
        showBookmark={false}
        customButtonsBeforeTitle={[]}
        showDfltBtns={false}
        showProfileButton={false}
        showBackButton={false}
        usingInMagicLink={true}
      />

      {/* Document Header - Displays version info and status */}
      <DocumentHeader
        type={currentRevision?.type_name}
        partNumber={currentRevision?.part_number}
        revision={currentRevision?.rev}
        revisionId={currentRevision?.procedure_revision_id}
        partId={currentRevision?.procedure_id}
        description={currentRevision?.part_description}
        status={currentStatus}
        currentRevision={currentRevision}
        revisionData={revisionData}
        isCheckboxEnabled={false}
        onCheckboxClick={null}
        showAdditionalInfo={false}
        onShowAdditionalInfoChange={null}
        lockErrorMessage={null}
        showCheckbox={false}
        magicUrl={true}
      />
            
      <RevisionTable
        revisionData={revisionData}
        revisionId={currentRevision?.procedure_revision_id}
        showCreateButton={false}
        isCreateButtonDisabled={true}
        canCreate={false}
        isReadOnly={true}
        partId={currentRevision?.procedure_id}
        partNumber={currentRevision?.part_number}
        type={currentRevision?.type_name}
        description={currentRevision?.description}
        myRevision={currentRevision?.my_revision}
        users={currentRevision?.users}
        approvedUsers={currentRevision?.approved_users}
        releasedUsers={currentRevision?.released_users}
        rule={currentRevision?.rule}
        onRevisionCreated={null}
        onCloseAdditionalInfo={null}
        showNavLink={false}
      />

      {/* Training Log Table - Displays training records */}
      <TrainingLogTable trainingLogs={[]} />


      {/* BOM Table - Bill of Materials */}
      <BOMTable type={currentRevision?.type_name} bomTable={bomTableData} />

      {/* Revision Nodes - Hierarchical section/step/substep structure */}
      {loading.revisionData ? (
        <InlineLoader text="Loading revision nodes..." height="300px" />
      ) : (
        <RenderRevisionNodes
          partRef={currentRevision?.part_ref}
          procedureRevRef={currentRevision?.procedure_revision_id}
          partNumber={currentRevision?.part_number}
          revision={currentRevision?.rev}
          disabled={true}
          onDataLoaded={setAllNodesData}
          refreshKey={"0"}
          isLock={isLock}
          isRevisionLocked={false}
          isLeader={true}
          magicUrl={true}
          showCheckbox={false}
        />
      )}

      
    </div>
  </>);
};

export default MagicView;
